// Interactive Features for Awasi Website
// Implements drag-to-scroll, pagination, and hover reveals

import { images } from '../components/shared/assets';
// import { SimplePushUpAnimation } from './SimplePushUpAnimation'; // DISABLED

// Enhanced Transform-Based Carousel with Seamless Vertical Transition
export const initializeDragScroll = (carouselId: string) => {
  const carousel = document.getElementById(carouselId);
  const carouselSection = carousel?.closest('.horizontal-carousel-section, .horizontal-scroll-carousel') as HTMLElement;
  if (!carousel || !carouselSection) return;

  // Scroll state management
  let scrollState: 'entering' | 'horizontal' | 'exiting' = 'horizontal';
  let currentTransform = 0;
  let maxTransform = 0;
  let velocity = 0;
  let isScrolling = false;
  let animationFrame: number | null = null;

  // Interaction state
  let isPointerDown = false;
  let isDragging = false;
  let startX = 0;
  let startY = 0;
  let startTransform = 0;
  let lastMoveTime = 0;
  let lastMoveX = 0;

  // Calculate maximum scroll distance
  const calculateMaxTransform = () => {
    // Check if using the standardized horizontal scroll carousel container
    const isStandardCarousel = carousel.classList.contains('horizontal-scroll-carousel__scroll-container');
    // For dining carousel, check if it's the dining carousel
    const isDiningCarousel = carousel.id === 'dining-carousel';
    
    if (isStandardCarousel) {
      // Brand homepage and Producing Nature carousel: calculate actual scroll width
      const viewportWidth = window.innerWidth;
      let trackWidth;
      
      // Check carousel type by looking at the carousel content
      const isProducingNature = carousel.querySelector('.producing-nature-frame');
      const isPremiumCarousel = carousel.querySelector('.carousel-slide');
      const isDestinationsCarousel = carousel.querySelector('.destinations-carousel__panel');
      const isWellnessCarousel = carousel.querySelector('.wellness-carousel__panel');
      
      if (isProducingNature) {
        // Producing Nature carousel: Frame widths: 2011 + 1440 + 1440 + 1440 + 1440 + 1440 + 571 + 869 = 10,651px
        trackWidth = 10651;
      } else if (isPremiumCarousel && !isProducingNature) {
        // Premium carousel: 1440 + 2309 + 1440 + 1446 = 6635px
        trackWidth = 6635;
      } else if (isDestinationsCarousel) {
        // Destinations carousel: Calculate based on number of destination panels
        const panels = carousel.querySelectorAll('.destinations-carousel__panel');
        trackWidth = panels.length * viewportWidth; // Each panel is full viewport width
      } else if (isWellnessCarousel) {
        // Wellness carousel: Calculate based on number of wellness panels
        const panels = carousel.querySelectorAll('.wellness-carousel__panel');
        trackWidth = panels.length * viewportWidth; // Each panel is full viewport width
      } else {
        // Brand homepage carousel: 100vw + 1043px + 100vw + 726px = total width
        trackWidth = viewportWidth + 1043 + viewportWidth + 726; // Diptych + Collage + Full + Outro
      }
      
      const containerWidth = carouselSection.clientWidth || window.innerWidth;
      maxTransform = Math.max(0, trackWidth - containerWidth);
    } else if (isDiningCarousel) {
      // Dining carousel: calculate exact panel widths
      // All panels are now 100vw: split + landscape + gallery + chef feature + chef profile + final split
      const viewportWidth = window.innerWidth;
      const trackWidth = viewportWidth * 6; // 6 full viewport panels
      const containerWidth = carouselSection.clientWidth || window.innerWidth;
      maxTransform = Math.max(0, trackWidth - containerWidth);
    } else {
      // Fallback for other carousels
      const carouselWidth = carousel.scrollWidth;
      const containerWidth = carousel.clientWidth;
      maxTransform = Math.max(0, carouselWidth - containerWidth);
    }
  };

  // Smooth animation with momentum
  const animateScroll = () => {
    if (!isScrolling && Math.abs(velocity) < 0.5) {
      animationFrame = null;
      return;
    }

    // Apply friction (increased to slow down carousel by 30%)
    velocity *= 0.93;
    
    // Update transform
    currentTransform += velocity;
    
    // Constrain bounds
    currentTransform = Math.max(0, Math.min(currentTransform, maxTransform));
    
    // Apply transform
    carousel.style.transform = `translateX(-${currentTransform}px)`;
    
    // Check if we're at the end and handle state transitions
    const atEnd = currentTransform >= maxTransform - 5;
    const atStart = currentTransform <= 5;
    
    if (atEnd && scrollState !== 'exiting') {
      scrollState = 'exiting';
    } else if (atStart && scrollState !== 'entering') {
      scrollState = 'entering';
    } else if (!atEnd && !atStart && scrollState !== 'horizontal') {
      scrollState = 'horizontal';
    }

    // Continue animation
    if (isScrolling || Math.abs(velocity) >= 0.5) {
      animationFrame = requestAnimationFrame(animateScroll);
    } else {
      isScrolling = false;
      animationFrame = null;
    }
  };

  // Start smooth animation
  const startAnimation = () => {
    if (!animationFrame) {
      isScrolling = true;
      animationFrame = requestAnimationFrame(animateScroll);
    }
  };

  // Handle pointer down (mouse/touch)
  const handlePointerDown = (clientX: number, clientY: number) => {
    isPointerDown = true;
    isDragging = false;
    startX = clientX;
    startY = clientY;
    startTransform = currentTransform;
    velocity = 0;
    lastMoveTime = Date.now();
    lastMoveX = clientX;
    
    carousel.style.cursor = 'grabbing';
    carousel.classList.add('active');
  };

  // Handle pointer move (mouse/touch)
  const handlePointerMove = (clientX: number, clientY: number) => {
    if (!isPointerDown) return;

    const deltaX = Math.abs(clientX - startX);
    const deltaY = Math.abs(clientY - startY);
    const currentTime = Date.now();
    
    // Start dragging if horizontal movement dominates
    if (!isDragging && (deltaX > 3 || deltaY > 3)) {
      if (deltaX >= deltaY) {
        isDragging = true;
      } else {
        return; // Vertical movement, don't interfere
      }
    }

    if (isDragging) {
      const moveDistance = clientX - startX;
      const newTransform = startTransform - moveDistance;
      
      // Apply bounds with slight elasticity at edges
      if (newTransform < 0) {
        currentTransform = newTransform * 0.3; // Elastic effect at start
      } else if (newTransform > maxTransform) {
        currentTransform = maxTransform + (newTransform - maxTransform) * 0.3; // Elastic effect at end
      } else {
        currentTransform = newTransform;
      }
      
      // Calculate velocity for momentum (reduced by 30%)
      const timeDiff = currentTime - lastMoveTime;
      if (timeDiff > 0) {
        velocity = (lastMoveX - clientX) / timeDiff * 11.2; // Reduced from 16 to 11.2 (30% slower)
      }
      
      lastMoveTime = currentTime;
      lastMoveX = clientX;
      
      // Apply transform immediately for responsive feel
      carousel.style.transform = `translateX(-${currentTransform}px)`;
    }
  };

  // Handle pointer up (mouse/touch)
  const handlePointerUp = () => {
    if (isDragging) {
      // Snap back from elastic bounds
      if (currentTransform < 0) {
        currentTransform = 0;
        velocity = 0;
      } else if (currentTransform > maxTransform) {
        currentTransform = maxTransform;
        velocity = 0;
      }
      
      // Start momentum animation
      startAnimation();
    }

    isPointerDown = false;
    isDragging = false;
    carousel.style.cursor = 'grab';
    carousel.classList.remove('active');
  };

  // Enhanced wheel handling with smooth transitions
  const handleWheel = (e: WheelEvent) => {
    // Check if we're in a state where we should allow vertical scroll
    if (scrollState === 'exiting' && e.deltaY > 0) {
      // At the end, allow vertical scroll to continue to next section
      return; // Don't prevent default, let it scroll the page
    }
    
    if (scrollState === 'entering' && e.deltaY < 0) {
      // At the start, allow vertical scroll to go to previous section
      return; // Don't prevent default, let it scroll the page
    }
    
    // We're in horizontal mode, handle horizontal scrolling
    e.preventDefault();
    e.stopPropagation();
    
    // Convert wheel delta to horizontal movement
    const delta = e.deltaY || e.deltaX;
    velocity += delta * 0.56; // Reduced wheel responsiveness (30% slower)
    
    startAnimation();
  };

  // Mouse event handlers
  const handleMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    handlePointerDown(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: MouseEvent) => {
    handlePointerMove(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    handlePointerUp();
  };

  const handleMouseLeave = () => {
    if (isPointerDown) {
      handlePointerUp();
    }
  };

  // Touch event handlers
  const handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 1) {
      handlePointerDown(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 1 && isDragging) {
      e.preventDefault(); // Only prevent if we're actively dragging
      handlePointerMove(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  const handleTouchEnd = () => {
    handlePointerUp();
  };

  // Initialize
  calculateMaxTransform();
  carousel.style.cursor = 'grab';
  carousel.style.transform = 'translateX(0px)';
  carousel.style.transition = 'none'; // Remove any CSS transitions

  // Add event listeners
  carousel.addEventListener('mousedown', handleMouseDown);
  carousel.addEventListener('mousemove', handleMouseMove);
  carousel.addEventListener('mouseup', handleMouseUp);
  carousel.addEventListener('mouseleave', handleMouseLeave);
  carousel.addEventListener('touchstart', handleTouchStart, { passive: true });
  carousel.addEventListener('touchmove', handleTouchMove, { passive: false });
  carousel.addEventListener('touchend', handleTouchEnd);
  
  // Add wheel listener to the section for better detection
  carouselSection.addEventListener('wheel', handleWheel, { passive: false });

  // Recalculate on resize
  const handleResize = () => {
    calculateMaxTransform();
  };
  window.addEventListener('resize', handleResize);

  // Cleanup function
  return () => {
    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
    }
    
    carousel.removeEventListener('mousedown', handleMouseDown);
    carousel.removeEventListener('mousemove', handleMouseMove);
    carousel.removeEventListener('mouseup', handleMouseUp);
    carousel.removeEventListener('mouseleave', handleMouseLeave);
    carousel.removeEventListener('touchstart', handleTouchStart);
    carousel.removeEventListener('touchmove', handleTouchMove);
    carousel.removeEventListener('touchend', handleTouchEnd);
    carouselSection.removeEventListener('wheel', handleWheel);
    window.removeEventListener('resize', handleResize);
  };
};

// Restaurant Image Pagination with Cross-fade
export const initializePagination = (paginationId: string) => {
  const pagination = document.getElementById(paginationId);
  if (!pagination) return;

  const restaurantImages = [
    images.interactiveImage1,
    images.interactiveImage2,
    images.interactiveImage3,
    images.interactiveImage4
  ];

  const restaurantSection = pagination.closest('.restaurant-feature');
  if (!restaurantSection) return;

  const paginationItems = pagination.querySelectorAll('span');
  
  paginationItems.forEach((item, index) => {
    item.addEventListener('click', () => {
      // Remove active class from all items
      paginationItems.forEach(p => p.classList.remove('active'));
      
      // Add active class to clicked item
      item.classList.add('active');
      
      // Cross-fade background image
      const newImage = restaurantImages[index];
      if (newImage) {
        // Create temporary overlay for smooth transition
        const overlay = document.createElement('div');
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100%';
        overlay.style.height = '100%';
        overlay.style.backgroundImage = `url('${newImage}')`;
        overlay.style.backgroundSize = 'cover';
        overlay.style.backgroundPosition = 'center';
        overlay.style.opacity = '0';
        overlay.style.transition = 'opacity 0.6s ease-in-out';
        overlay.style.zIndex = '1';
        
        (restaurantSection as HTMLElement).appendChild(overlay);
        
        // Fade in new image
        setTimeout(() => {
          overlay.style.opacity = '1';
        }, 50);
        
        // Update main background and cleanup
        setTimeout(() => {
          (restaurantSection as HTMLElement).style.backgroundImage = `url('${newImage}')`;
          (restaurantSection as HTMLElement).removeChild(overlay);
        }, 650);
      }
    });
  });
};

// Blog Hover-Reveal Images
export const initializeHoverReveals = (listId: string, imageId: string) => {
  const blogList = document.getElementById(listId);
  const revealImage = document.getElementById(imageId);
  
  if (!blogList || !revealImage) return;

  const blogImages = [
    images.galleryOfferBackground,
    images.interactiveImage5,
    images.interactiveImage6,
    images.interactiveImage3
  ];

  const blogItems = blogList.querySelectorAll('.blog-item');
  const imageElement = revealImage.querySelector('img') as HTMLImageElement;

  if (!imageElement) return;

  blogItems.forEach((item, index) => {
    item.addEventListener('mouseenter', () => {
      const newImage = blogImages[index];
      if (newImage) {
        // Fade out current image
        imageElement.style.opacity = '0';
        
        // Change image after fade out
        setTimeout(() => {
          imageElement.src = newImage;
          imageElement.style.opacity = '1';
        }, 300);
      }
    });

    item.addEventListener('mouseleave', () => {
      // Fade out image completely when not hovering
      imageElement.style.opacity = '0';
    });
  });

  // Initial state - hidden
  imageElement.style.opacity = '0';
  imageElement.style.transition = 'opacity 0.3s ease-in-out';
};

// Parallax Effect for Image Collages
export const initializeParallax = () => {
  const parallaxElements = document.querySelectorAll('[data-parallax]');
  
  const handleScroll = () => {
    const scrollTop = window.pageYOffset;
    
    parallaxElements.forEach(element => {
      const rect = element.getBoundingClientRect();
      const speed = parseFloat(element.getAttribute('data-parallax') || '0.5');
      
      if (rect.bottom >= 0 && rect.top <= window.innerHeight) {
        const yPos = -(scrollTop * speed);
        (element as HTMLElement).style.transform = `translateY(${yPos}px)`;
      }
    });
  };

  // Throttled scroll handler
  let ticking = false;
  const throttledScroll = () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        handleScroll();
        ticking = false;
      });
      ticking = true;
    }
  };

  window.addEventListener('scroll', throttledScroll, { passive: true });
  
  return () => {
    window.removeEventListener('scroll', throttledScroll);
  };
};

// Initialize all interactive features
export const initializeAllFeatures = () => {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeFeatures();
    });
  } else {
    initializeFeatures();
  }
};

const initializeFeatures = () => {
  const cleanupFunctions: Array<() => void> = [];
  
  // Initialize all drag-to-scroll carousels with standardized IDs
  const carouselIds = [
    'draggable-carousel',           // Main carousel used across all pages (brand homepage, producing nature, destinations, premium, wellness)
    'blog-carousel',                // Blog section carousel
    'blog-related-carousel',        // Related blog posts carousel
  ];
  
  // Initialize each carousel that exists in the DOM
  carouselIds.forEach(id => {
    const carousel = document.getElementById(id);
    if (carousel) {
      const cleanup = initializeDragScroll(id);
      if (cleanup) cleanupFunctions.push(cleanup);
    }
  });
  
  // Initialize restaurant pagination
  initializePagination('restaurant-pagination');
  
  // Initialize blog hover reveals
  initializeHoverReveals('hover-blog-list', 'hover-reveal-image');
  
  // Initialize parallax effects
  const parallaxCleanup = initializeParallax();
  if (parallaxCleanup) cleanupFunctions.push(parallaxCleanup);
  
  // Initialize simple push-up animation for hero-to-carousel transitions
  // DISABLED - animations not working properly
  // const pushUpAnimation = new SimplePushUpAnimation();
  // cleanupFunctions.push(() => pushUpAnimation.destroy());
  
  // Return cleanup function that cleans up all features
  return () => {
    cleanupFunctions.forEach(cleanup => cleanup());
  };
};
